import logoDark from "../assets/images/logo-dark.svg";
import logoLight from "../assets/images/logo-light.svg";
import headerLogoDark from "../assets/images/logo-dark.png";
import headerLogoLight from "../assets/images/logo-light.png";
import logoSmallDark from "../assets/images/logo-sm.png";
import logoSmallLight from "../assets/images/logo-sm-light.png";
import { domainThemeOverrides } from "./domainThemes";
import { AppTheme, DomainThemeOverrides, ThemeOverride } from "./types";

export const defaultTheme: AppTheme = {
    brand: {
        companyName: "Galactic Group",
        shortName: "Galactic Group",
        productName: "Office",
    },
    assets: {
        logoDark,
        logoLight,
        headerLogoDark,
        headerLogoLight,
        logoSmallDark,
        logoSmallLight,
        favicon: "/logo.ico",
        unreadFavicon: "/mail.ico",
        appleTouchIcon: "/apple-touch-icon.png",
        pwaIcon192: "/icon-192.png",
        pwaIcon512: "/icon-512.png",
    },
    colors: {
        primary: "#0576b9",
        primaryHover: "#04649d",
        primaryActive: "#045e94",
        linkHover: "#045e94",
        focusBorder: "#82bbdc",
        primaryContrast: "#ffffff",
        sidebarBackground: "#121e2d",
        topbarBackground: "#0576b9",
        browserTheme: "#121e2d",
        pageBackground: "#f8f8f8",
        portalPrimary: "#2f6fed",
        portalPrimaryStrong: "#245fdb",
    },
    contact: {
        supportEmail: "support@galactic-group.nl",
    },
    domain: {
        root: "galactic-group.nl",
        protocol: "https",
    },
    urls: {
        auth: "https://auth.galactic-group.nl",
        account: "https://account.galactic-group.nl",
        office: "https://office.galactic-group.nl",
    },
};

const normalizeHostname = (hostname: string): string =>
    hostname.trim().toLowerCase().replace(/\.$/, "").replace(/:\d+$/, "");

const mergeTheme = (base: AppTheme, override: ThemeOverride): AppTheme => ({
    brand: { ...base.brand, ...override.brand },
    assets: { ...base.assets, ...override.assets },
    colors: { ...base.colors, ...override.colors },
    contact: { ...base.contact, ...override.contact },
    domain: { ...base.domain, ...override.domain },
    urls: { ...base.urls, ...override.urls },
});

const mixHexColor = (color: string, target: number, amount: number): string | undefined => {
    const normalized = color.replace(/^#/, "");
    const expanded = normalized.length === 3
        ? normalized.split("").map(character => character + character).join("")
        : normalized;
    if (!/^[0-9a-f]{6}$/i.test(expanded)) {
        return undefined;
    }

    const channels = [0, 2, 4].map(offset => parseInt(expanded.slice(offset, offset + 2), 16));
    const mixed = channels.map(channel => Math.round(channel + (target - channel) * amount));
    return `#${mixed.map(channel => channel.toString(16).padStart(2, "0")).join("")}`;
};

const applyDerivedColorOverrides = (theme: AppTheme, override: ThemeOverride): AppTheme => {
    const primary = override.colors?.primary;
    if (!primary) {
        return theme;
    }

    return {
        ...theme,
        colors: {
            ...theme.colors,
            primaryHover: override.colors?.primaryHover ?? mixHexColor(primary, 0, 0.15) ?? theme.colors.primaryHover,
            primaryActive: override.colors?.primaryActive ?? mixHexColor(primary, 0, 0.2) ?? theme.colors.primaryActive,
            linkHover: override.colors?.linkHover ?? mixHexColor(primary, 0, 0.2) ?? theme.colors.linkHover,
            focusBorder: override.colors?.focusBorder ?? mixHexColor(primary, 255, 0.5) ?? theme.colors.focusBorder,
            topbarBackground: override.colors?.topbarBackground ?? primary,
            portalPrimary: override.colors?.portalPrimary ?? primary,
            portalPrimaryStrong: override.colors?.portalPrimaryStrong
                ?? mixHexColor(primary, 0, 0.2)
                ?? theme.colors.portalPrimaryStrong,
        },
    };
};

const applyDerivedAssetOverrides = (theme: AppTheme, override: ThemeOverride): AppTheme => ({
    ...theme,
    assets: {
        ...theme.assets,
        headerLogoDark: override.assets?.headerLogoDark ?? override.assets?.logoDark ?? theme.assets.headerLogoDark,
        headerLogoLight: override.assets?.headerLogoLight ?? override.assets?.logoLight ?? theme.assets.headerLogoLight,
        logoSmallDark: override.assets?.logoSmallDark ?? override.assets?.logoDark ?? theme.assets.logoSmallDark,
        logoSmallLight: override.assets?.logoSmallLight ?? override.assets?.logoLight ?? theme.assets.logoSmallLight,
    },
});

export const resolveTheme = (
    hostname: string,
    overrides: DomainThemeOverrides = domainThemeOverrides,
): AppTheme => {
    const normalizedHostname = normalizeHostname(hostname);
    const matchingRootDomain = Object.keys(overrides)
        .map(normalizeHostname)
        .filter(rootDomain =>
            normalizedHostname === rootDomain || normalizedHostname.endsWith(`.${rootDomain}`)
        )
        .sort((left, right) => right.length - left.length)[0];

    if (!matchingRootDomain) {
        return defaultTheme;
    }

    const configuredRootDomain = Object.keys(overrides).find(
        rootDomain => normalizeHostname(rootDomain) === matchingRootDomain
    ) as string;
    const override = overrides[configuredRootDomain];
    const mergedTheme = mergeTheme(defaultTheme, override);
    const theme = applyDerivedAssetOverrides(
        applyDerivedColorOverrides(mergedTheme, override),
        override,
    );

    return {
        ...theme,
        domain: {
            ...theme.domain,
            root: matchingRootDomain,
        },
    };
};

export const getCurrentTheme = (): AppTheme =>
    resolveTheme(typeof window === "undefined" ? defaultTheme.domain.root : window.location.hostname);

export const getThemeHostname = (theme: AppTheme, subdomain?: string): string =>
    subdomain ? `${subdomain}.${theme.domain.root}` : theme.domain.root;

export const getThemeUrl = (theme: AppTheme, subdomain: string, path = ""): string => {
    const normalizedPath = path === "" || path.startsWith("/") ? path : `/${path}`;
    return `${theme.domain.protocol}://${getThemeHostname(theme, subdomain)}${normalizedPath}`;
};

export const getThemeApplicationUrl = (
    theme: AppTheme,
    application: keyof AppTheme["urls"],
    path = "",
): string => {
    const baseUrl = theme.urls[application].replace(/\/$/, "");
    const normalizedPath = path === "" || path.startsWith("/") ? path : `/${path}`;
    return `${baseUrl}${normalizedPath}`;
};
